import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import { formatCurrency, useAppConfig, usePermission } from "../../../shared";
import { errorMessage } from "../../../utils";
import { cancelAndReissueFtiApi, getFtiDetailsApi } from "../apis";
import { useFtiPdfExport } from "./useFtiPdfExport";

export const useFullTaxInvoiceDetails = () => {
  const { ftiId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const basePath = isStoreAdmin ? "/store-admin" : "";
  const { currencyPrecision } = useAppConfig();
  const isEditable = usePermission("fti-modify");
  const { isExporting, exportFtiPdf } = useFtiPdfExport();
  const [state, setState] = useImmer({
    details: null,
    isLoading: true,
    isReissueOpen: false,
    isReprintOpen: false,
  });

  const breadcrumbs = [
    { name: "Dashboard", href: basePath || "/" },
    { name: "Full Tax Invoices", href: `${basePath}/full-tax-invoice` },
    { name: state.details?.fti_invoice_number ?? "" },
  ];

  const details = state.details ?? {};
  const reissueInitialValues = {
    business_name: details.business_name ?? "",
    business_branch: details.business_branch ?? "",
    full_business_address: details.full_business_address ?? "",
    business_state: details.business_state ?? "",
    business_postcode: details.business_postcode ?? "",
    abn_number: details.abn_number ?? "",
    business_country_code: details.country_code ?? "",
    business_phone: details.phone ?? "",
  };

  const reprintRemarks = `ใบแทนใบกำกับภาษีออกให้ครั้งที่ ${
    (details.rcipet_history?.length ?? 0) + 1
  } เนื่องจากต้นฉบับสูญหาย`;

  const getDetails = async (id, showLoader = true) => {
    setState((draft) => {
      draft.isLoading = showLoader;
    });
    try {
      const response = await getFtiDetailsApi(id);
      const { success, message, data } = response;
      if (success) {
        setState((draft) => {
          draft.details = data;
        });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ?? "Failed to fetch full tax invoice";
      errorMessage(message);
    } finally {
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const toggleReprint = (isReprintOpen) => {
    setState((draft) => {
      draft.isReprintOpen = isReprintOpen;
    });
  };

  const handlePrint = async () => {
    await exportFtiPdf(state.details);
    toggleReprint(false);
    getDetails(ftiId, false);
  };

  const formatAmount = (amount) =>
    amount === null || amount === undefined
      ? "--"
      : formatCurrency(amount, { currencyPrecision });

  const toggleReissue = (isReissueOpen) => {
    setState((draft) => {
      draft.isReissueOpen = isReissueOpen;
    });
  };

  const handleReissueFti = (payload) => cancelAndReissueFtiApi(ftiId, payload);

  const handleFtiReissued = (fti) => {
    toggleReissue(false);
    navigate(`${basePath}/full-tax-invoice/${fti.id}/details`);
  };

  React.useEffect(() => {
    getDetails(ftiId);
  }, [ftiId]);

  return {
    state,
    isStoreAdmin,
    isEditable,
    breadcrumbs,
    reissueInitialValues,
    reprintRemarks,
    formatAmount,
    isPrinting: isExporting,
    handlePrint,
    handleOpenReprint: () => toggleReprint(true),
    handleCloseReprint: () => toggleReprint(false),
    handleOpenReissue: () => toggleReissue(true),
    handleCloseReissue: () => toggleReissue(false),
    handleReissueFti,
    handleFtiReissued,
  };
};
